'use client';

import React, {
    useCallback,
    useState,
    useMemo,
    useRef,
    useLayoutEffect,
} from 'react';
import Button from '@frontend/jetlend-web-ui/src/ui/inputs/Button/Button';
import { buildClassNames } from '@frontend/jetlend-web-ui/src/utils/classNameUtils';
import {
    AnimatePresence,
    motion,
} from 'framer-motion';
import {
    isMobile,
    useWindowSize,
} from '@frontend/jetlend-web-ui/src/utils/responsive';
import {
    TechnologiesFeaturesWithBlocksSectionType,
    TechnologiesFeatureType,
    TechnologiesFeatureTypeMap,
} from '@app/models/features/technologies';
import Section from '@app/ui/Section/Section';
import styles from './FunctionalFeatures.module.scss';
import FeatureContentBlock from '@app/components/FeatureContentBlock/FeatureContentBlock';

/**
 * Высота хедера блока, чтобы правильно отобразить весь блока
 */
const BLOCK_HEADER_HEIGHT = 100;

/**
 * Компонент для отображения блока "Функционал платформы"
 */
export default function FunctionalFeatures({
    blocks,
    title,
    title_mobile,
}: TechnologiesFeaturesWithBlocksSectionType) {
    const [selectedType, setSelectedType] = useState<TechnologiesFeatureType>(TechnologiesFeatureType.Investor);
    const [containerHeight, setContainerHeight] = useState(0);

    const investorRef = useRef<HTMLDivElement>(null);
    const borrowerRef = useRef<HTMLDivElement>(null);

    const { width } = useWindowSize();
    const isMobileScreen = isMobile(width);

    const didClick = useCallback((type: TechnologiesFeatureType) => {
        setSelectedType(type);
    }, []);

    const investorBlocks = useMemo(() => {
        return blocks.filter(item => item.value === TechnologiesFeatureType.Investor);
    }, [blocks]);

    const borrowerBlocks = useMemo(() => {
        return blocks.filter(item => item.value === TechnologiesFeatureType.Borrower);
    }, [blocks]);

    const contentClassName = buildClassNames(
        styles,
        [
            'content',
            selectedType === TechnologiesFeatureType.Investor
                ? 'four'
                : 'three',
        ]
    );


    useLayoutEffect(() => {
        if (selectedType === TechnologiesFeatureType.Borrower) {
            setContainerHeight(borrowerRef.current?.getBoundingClientRect()?.height + BLOCK_HEADER_HEIGHT);
            return;
        }

        setContainerHeight(investorRef.current?.getBoundingClientRect()?.height + BLOCK_HEADER_HEIGHT);
    }, [selectedType, investorRef.current, borrowerRef.current]);

    return (
        <Section
            filled
            title={title}
            mobileTitle={title_mobile}
            animation="none"
            title_align="center"
        >
            <div
                className={styles['container']}
                style={{
                    height: isMobileScreen && containerHeight,
                }}
            >
                <div className="d-flex d-flex--gap-5">
                    {Object.entries(TechnologiesFeatureTypeMap).map(([key, value]) => (
                        <Button
                            text={value}
                            key={key}
                            type="primary"
                            outline={selectedType !== key}
                            size="xsmall"
                            containerClassName={selectedType === key && 'a-disabled'}
                            onClick={() => didClick(key as TechnologiesFeatureType)}
                        />
                    ))}
                </div>
                <AnimatePresence>
                    {selectedType === TechnologiesFeatureType.Investor
                        ? (
                            <motion.div
                                key='investor'
                                className={contentClassName}
                                initial={{
                                    opacity: 0,
                                    translateX: -200,
                                    filter: 'blur(10px)',
                                }}
                                animate={{
                                    opacity: 1,
                                    translateX: 0,
                                    filter: 'blur(0px)',
                                }}
                                exit={{
                                    opacity: 0,
                                    translateX: -200,
                                    filter: 'blur(10px)',
                                }}
                                transition={{
                                    duration: 0.3,
                                }}
                                ref={investorRef}
                            >
                                {investorBlocks.map(block => (
                                    <FeatureContentBlock
                                        key={block.title}
                                        variant="gray"
                                        {...block}
                                    />
                                ))}
                            </motion.div>
                        )
                        : (
                            <motion.div
                                key='borrower'
                                className={contentClassName}
                                initial={{
                                    opacity: 0,
                                    translateX: 200,
                                    filter: 'blur(10px)',
                                }}
                                animate={{
                                    opacity: 1,
                                    translateX: 0,
                                    filter: 'blur(0px)',
                                }}
                                exit={{
                                    opacity: 0,
                                    translateX: 200,
                                    filter: 'blur(10px)',
                                }}
                                transition={{
                                    duration: 0.3,
                                }}
                                ref={borrowerRef}
                            >
                                {borrowerBlocks.map(block => (
                                    <FeatureContentBlock
                                        key={block.title}
                                        variant="gray"
                                        {...block}
                                    />
                                ))}
                            </motion.div>
                        )
                    }
                </AnimatePresence>
            </div>
        </Section>
    );
};
